import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { useMemo, useState } from "react";
import * as yup from "yup";
import { I18n } from "react-redux-i18n";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import FormButton from "./FormButton";
import { Auth } from "aws-amplify";
import { normalizeUser } from "../../utils/normalizeUser";
import { connect } from "react-redux";
import { setUserProfile } from "../../state/actions/user";
import { AUTHENTICATOR_SIGNUP } from "./constants";

const ResetPassword = ({ setUserProfile, setAuthScreen, user }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [loader, setLoader] = useState(false);

  const schema = useMemo(() => {
    return yup.object({
      password: yup
        .string()
        .required(I18n.t("authenticator.required"))
        .min(6, I18n.t("authenticator.minLength")),
      confirmPassword: yup
        .string()
        .required(I18n.t("authenticator.passRepeatRequired"))
        .test(
          "passwords-match",
          I18n.t("authenticator.passwordsNotMatch"),
          function (value) {
            return this.parent.password === value;
          }
        ),
    });
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors },
    formState: { isSubmitted },
  } = useForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoader(true);

    try {
      const verifiedUser = await Auth.completeNewPassword(user, data.password);

      setUserProfile(normalizeUser(verifiedUser));

      return;
    } catch (e) {
      console.log(e);
    }

    setLoader(false);
  };

  return (
    <Grid
      container
      spacing={2}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Typography
          variant="h4"
          color="primary"
          style={{
            fontFamily: "'Paytone One', sans- serif",
            marginBottom: "4px",
          }}
        >
          {I18n.t("authenticator.resetPassword")}
        </Typography>
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="password"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              label={I18n.t("authenticator.password")}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              {...field}
              error={isSubmitted && Boolean(errors.password)}
              helperText={errors?.password?.message || ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="confirmPassword"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              label={I18n.t("authenticator.repeatPass")}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              {...field}
              error={isSubmitted && Boolean(errors.confirmPassword)}
              helperText={errors?.confirmPassword?.message || ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>

      <Box style={{ alignSelf: "center", margin: "20px auto" }}>
        <FormButton
          {...{
            label: I18n.t("authenticator.done"),
            loader,
          }}
        />
      </Box>

      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography style={{ marginRight: "4px" }} color="textSecondary">
          {I18n.t("authenticator.notMember")}
        </Typography>

        <Button
          type="submit"
          color="primary"
          onClick={() => setAuthScreen(AUTHENTICATOR_SIGNUP)}
        >
          {I18n.t("authenticator.signUp")}
        </Button>
      </Grid>
    </Grid>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setUserProfile: (user) => dispatch(setUserProfile(user)),
});

export default connect(null, mapDispatchToProps)(ResetPassword);
